// useBarcodeGenerator.ts
import { useEffect } from "react";
import bwipjs from "bwip-js/browser";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import { updateNodeProperty } from "@builder/store/slices/BuilderSlice/builderSlice";
import { getSelectedNode } from "@builder/store/slices/BuilderSlice/builderSelectors";
import { BarcodeNode } from "@common/build/types";
import useDebouncedCallback from "./useDebounceCallback";

const generateBarcode = (
  type: string,
  content: string,
  includeText: boolean,
  color?: string
) => {
  return bwipjs.toSVG({
    bcid: type,
    text: content,
    includetext: includeText,
    barcolor: color || "000000",
    textcolor: color || "000000",
  });
};

const useBarcodeGenerator = (textType: string, setError?: (error: string) => void) => {
  const dispatch = useAppDispatch();
  const selectedNode = useAppSelector(getSelectedNode) as BarcodeNode;

  // 2) Wrap the actual "generate and dispatch" logic in a debounced callback
  const debouncedGenerateBarcode = useDebouncedCallback(
    (bcid: string, content: string, displayText: boolean, color?: string) => {
      try {
        const svg = generateBarcode(bcid, content, displayText, color);
        dispatch(updateNodeProperty({ propertyPath: ["svg"], value: svg }));
        if (setError) setError("");
      } catch (e) {
        let error = (e as Error).message;
        try {
          error = error.split(":")[1];
        } catch {
          error = "Invalid barcode content";
        }
        if (setError) setError(error);
      }
    },
    500 // <--- Debounce delay in ms; feel free to adjust
  );

  useEffect(() => {
    // Ensure we have defaults in place
    if (!selectedNode.barcode) {
      dispatch(
        updateNodeProperty({
          propertyPath: ["barcode"],
          value: {
            bcid: "code128",
            content: "12345678",
            displayText: true,
            color: "#000000",
          },
        })
      );
      return;
    }

    if (!selectedNode.barcode.bcid) {
      dispatch(
        updateNodeProperty({
          propertyPath: ["barcode"],
          value: { bcid: "code128", displayText: true },
        })
      );
    }

    // Build the content string
    const content =
      textType === "db"
        ? (selectedNode.barcode.prepend || "") +
          selectedNode.dynamicData +
          (selectedNode.barcode.append || "")
        : selectedNode.barcode.content || "12345678";

    // 3) Call the debounced generator instead of immediately generating
    debouncedGenerateBarcode(
      selectedNode.barcode.bcid || "code128",
      content,
      selectedNode.barcode.displayText || false,
      selectedNode.barcode.color
    );
  }, [
    selectedNode?.barcode?.bcid,
    selectedNode?.barcode?.content,
    selectedNode?.barcode?.displayText,
    selectedNode?.barcode?.color,
    selectedNode?.barcode?.prepend,
    selectedNode?.barcode?.append,
    selectedNode?.dynamicData,
    textType,
    dispatch,
    debouncedGenerateBarcode,
  ]);
};

export default useBarcodeGenerator;
